/**
 * Logger condicional para desarrollo/producción
 * En producción solo se muestran errores y warnings
 * En desarrollo se muestran todos los logs
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private isDev = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;
  
  /**
   * Logs generales - solo en desarrollo
   */
  log(...args: any[]) {
    if (this.isDev) {
      console.log(...args);
    }
  }
  
  /**
   * Información - solo en desarrollo
   */
  info(...args: any[]) {
    if (this.isDev) {
      console.info(...args);
    }
  }
  
  /**
   * Warnings - siempre se muestran pero con prefijo en producción
   */
  warn(...args: any[]) {
    if (this.isProduction) {
      console.warn('[WARN]', ...args);
    } else {
      console.warn(...args);
    }
  }
  
  /**
   * Errores - siempre se muestran con contexto adicional
   */
  error(...args: any[]) {
    if (this.isProduction) {
      console.error('[ERROR]', new Date().toISOString(), ...args);
    } else {
      console.error(...args);
    }
  }
  
  /**
   * Debug detallado - solo en desarrollo
   */
  debug(...args: any[]) {
    if (this.isDev) {
      console.debug('[DEBUG]', ...args);
    }
  }
  
  /**
   * Agrupa logs - solo en desarrollo
   */
  group(label: string) {
    if (this.isDev) {
      console.group(label);
    }
  }
  
  groupEnd() {
    if (this.isDev) {
      console.groupEnd();
    }
  }
  
  /**
   * Tabla - solo en desarrollo
   */
  table(data: any) {
    if (this.isDev) {
      console.table(data);
    }
  }
  
  /**
   * Time tracking - solo en desarrollo
   */
  time(label: string) {
    if (this.isDev) {
      console.time(label);
    }
  }
  
  timeEnd(label: string) {
    if (this.isDev) {
      console.timeEnd(label);
    }
  }
}

/**
 * Instancia singleton del logger
 * Usar en todo el código en lugar de console.*
 * 
 * @example
 * import { logger } from '@/utils/logger';
 * 
 * logger.log('Debug info');      // Solo en desarrollo
 * logger.info('Information');    // Solo en desarrollo
 * logger.warn('Warning');        // Siempre
 * logger.error('Error', error);  // Siempre con timestamp
 */
export const logger = new Logger();
